import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  CustomPlanTemplatesFilter,
  PerformanceQuality,
  PlanCommitmentLevel,
  PlanTrainingMode,
  usePerformanceQualityAttributesQuery,
} from '../../../../graphql/types';
import { TrackingEventData } from '../../../../hooks/useTracking';
import {
  EventLabels,
  EventSubCategories,
} from '../../../../utils/trackingEvents';
import Button, { ButtonVariant } from '../../../reusable/Button';
import { OptionType, Select } from '../../../reusable/Select';
import { FocusAreas } from '../util';

const StyledFilterContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const defaultValues = {
  focusArea: { value: undefined, label: 'Focus Area' },
  targetedPerformanceQualities: {
    value: undefined,
    label: 'Performance Quality',
  },
  commitmentLevel: { value: undefined, label: 'Commitment' },
  phase: { value: undefined, label: 'Phase' },
  trainingMode: { value: undefined, label: 'Training Mode' },
};
interface GameplanTemplateFiltersProps {
  setFilter: (filter: CustomPlanTemplatesFilter) => void;
  trackingData?: TrackingEventData;
}

export const GameplanTemplateFilters: React.FC<
  GameplanTemplateFiltersProps
> = ({ setFilter, trackingData }) => {
  const { watch, control, reset } = useForm<{
    commitmentLevel: OptionType<string>;
    targetedPerformanceQualities: OptionType<string>;
    phase: OptionType<number>;
    trainingMode: OptionType<string>;
    focusArea: OptionType<string>;
  }>({
    defaultValues,
  });
  const { data: pqAttributesData } = usePerformanceQualityAttributesQuery();
  const pqAttributes = pqAttributesData?.performanceQualityAttributes;

  const commitmentLevel = watch('commitmentLevel');
  const targetedPerformanceQualities = watch('targetedPerformanceQualities');
  const phase = watch('phase');
  const trainingMode = watch('trainingMode');
  const focusArea = watch('focusArea');

  useEffect(() => {
    setFilter({
      ...(commitmentLevel.value
        ? {
            commitmentLevel: [commitmentLevel.value as PlanCommitmentLevel],
          }
        : {}),
      ...(targetedPerformanceQualities.value
        ? {
            targetedPerformanceQualities: [
              targetedPerformanceQualities.value as PerformanceQuality,
            ],
          }
        : {}),
      ...(phase.value !== undefined
        ? {
            phase: [phase.value],
          }
        : {}),
      ...(trainingMode.value
        ? {
            trainingMode: [trainingMode.value as PlanTrainingMode],
          }
        : {}),
      ...(focusArea.value
        ? {
            focusArea: [focusArea.value],
          }
        : {}),
    });
  }, [
    commitmentLevel,
    targetedPerformanceQualities,
    phase,
    trainingMode,
    focusArea,
    setFilter,
  ]);

  const trackingDefaults = {
    ...trackingData,
    event_subcategory: EventSubCategories.Filters,
  };

  return (
    <StyledFilterContainer>
      <Controller
        control={control}
        name="focusArea"
        render={({ field }) => (
          <Select
            {...field}
            multiple={false}
            options={[
              defaultValues.focusArea,
              ...Object.values(FocusAreas).map((focusArea) => ({
                label: focusArea,
                value: focusArea,
              })),
            ]}
            trackingData={{ ...trackingDefaults, event_label: 'Focus Area' }}
          />
        )}
      />
      <Controller
        control={control}
        name="targetedPerformanceQualities"
        render={({ field }) => (
          <Select
            {...field}
            multiple={false}
            options={[
              defaultValues.targetedPerformanceQualities,
              ...(pqAttributes ?? [])
                .filter(
                  (pq) => pq.value !== PerformanceQuality.LoadAccumulation,
                )
                .map((pq) => ({
                  label: pq.name,
                  value: pq.value,
                })),
            ]}
            trackingData={{
              ...trackingDefaults,
              event_label: 'Performance Qualities',
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="commitmentLevel"
        render={({ field }) => (
          <Select
            {...field}
            multiple={false}
            options={[
              defaultValues.commitmentLevel,
              {
                label: PlanCommitmentLevel.Low,
                value: PlanCommitmentLevel.Low,
              },
              {
                label: PlanCommitmentLevel.Medium,
                value: PlanCommitmentLevel.Medium,
              },
              {
                label: PlanCommitmentLevel.High,
                value: PlanCommitmentLevel.High,
              },
            ]}
            trackingData={{
              ...trackingDefaults,
              event_label: 'Commitment Level',
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="phase"
        render={({ field }) => (
          <Select
            {...field}
            multiple={false}
            options={[
              defaultValues.phase,
              { value: 1, label: 'Phase 1' },
              { value: 2, label: 'Phase 2' },
            ]}
            trackingData={{
              ...trackingDefaults,
              event_label: 'Phase',
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="trainingMode"
        render={({ field }) => (
          <Select
            {...field}
            multiple={false}
            options={[
              defaultValues.trainingMode,
              {
                label: 'On-Demand Training Classes',
                value: PlanTrainingMode.Classes,
              },
              {
                label: 'Training Plan',
                value: PlanTrainingMode.TrainingPlan,
              },
            ]}
            trackingData={{
              ...trackingDefaults,
              event_label: 'Training Mode',
            }}
          />
        )}
      />
      <Button
        variant={ButtonVariant.Outline}
        onClick={() => reset(defaultValues)}
        trackingData={{
          ...trackingDefaults,
          event_label: EventLabels.Reset,
        }}
      >
        Reset
      </Button>
    </StyledFilterContainer>
  );
};
